import { Button, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useCallback, useState } from "react";
import { APIClient, BTC_NETWORK } from "../constants";
import { useWalletContext } from "../context/wallet";
import { useFeeEstimate } from "../hooks/useFeeEstimate";
import { walletProvider } from "../services/wallet";
import { buildMempoolTxnLink, errorToMessage } from "../utils";

export const WithdrawButton = ({ tokenIds }: { tokenIds: string[] }) => {
  const [loading, setLoading] = useState(false);
  const { multiSigWallet, paymentPubkey, paymentAddress, ordinalsPubkey, ordinalsAddressType } =
    useWalletContext();
  const feeEstimate = useFeeEstimate();

  const handleWithdraw = useCallback(async () => {
    setLoading(true);

    try {
      if (!multiSigWallet.data || !paymentPubkey || !paymentAddress) {
        return;
      }
      const { data } = await APIClient.get(
        `/mswallets/${multiSigWallet.data?.address}/psbt/withdraw`,
        {
          params: {
            feeSatsPerVbyte:
              BTC_NETWORK === "mainnet"
                ? feeEstimate.data?.halfHourFee?.satsPerVb
                : 10,
            userPaymentType: ordinalsAddressType,
            userPublicKey: paymentPubkey,
            outputs: [],
            tokenIds,
          },
        }
      );
      const { signedPsbt } = await walletProvider.signTransaction(
        data.psbt,
        [
          {
            address: paymentAddress,
            indexesToSign: data.toSignInputs,
          },
        ]
      );

      const { data: withdrawData } = await APIClient.post(
        `/mswallets/${multiSigWallet.data.address}/withdraw`,
        {
          psbt: signedPsbt,
          signerPaymentType: ordinalsAddressType,
          signerPublicKey: ordinalsPubkey,
          msWalletOwnerPublicKey: paymentPubkey,
        }
      );
      if (withdrawData) {
        notifications.show({
          autoClose: 15000,
          title: "Withdraw submitted",
          message: (
            <Text
              component="a"
              color="blue"
              underline
              href={buildMempoolTxnLink(withdrawData.transactionId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View transaction
            </Text>
          ),
        });
      }
    } catch (err) {
      notifications.show({
        autoClose: 15000,
        title: "Withdraw failed",
        message: errorToMessage(err),
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [
    feeEstimate.data?.halfHourFee?.satsPerVb,
    multiSigWallet.data,
    ordinalsPubkey,
    paymentAddress,
    paymentPubkey,
    ordinalsAddressType,
    tokenIds,
  ]);

  return (
    <Button onClick={handleWithdraw} loading={loading}>
      {loading ? "Withdrawing..." : "Withdraw"}
    </Button>
  );
};
